import React, { useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

type MachineWithCoords = {
  id: string;
  name: string;
  provider: string;
  status?: string;
  latitude: number;
  longitude: number;
  location?: string | null;
  address?: string | null;
};

const defaultCenter: [number, number] = [41.9028, 12.4964]; // Roma, Italia

// Icona marker custom (evita problemi con il path default di Leaflet)
const createIcon = () =>
  L.divIcon({
    className: "custom-marker",
    html: `<div style="
      width: 28px;
      height: 28px;
      background: #6366f1;
      border: 2px solid white;
      border-radius: 50%;
      box-shadow: 0 2px 6px rgba(0,0,0,0.3);
    "></div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });

function MapBounds({ machines }: { machines: MachineWithCoords[] }) {
  const map = useMap();
  React.useEffect(() => {
    if (machines.length < 2) return;
    const bounds = L.latLngBounds(
      machines.map((m) => [m.latitude, m.longitude] as L.LatLngTuple)
    );
    map.fitBounds(bounds, { padding: [50, 50], maxZoom: 17 });
  }, [map, machines]);
  return null;
}

export function MachinesMapWidget({
  machines,
  providerLabel,
}: {
  machines: MachineWithCoords[];
  providerLabel: string;
}) {
  const mapCenter = useMemo((): [number, number] => {
    if (machines.length === 0) return defaultCenter;
    const avgLat =
      machines.reduce((s, m) => s + m.latitude, 0) / machines.length;
    const avgLng =
      machines.reduce((s, m) => s + m.longitude, 0) / machines.length;
    return [avgLat, avgLng];
  }, [machines]);

  const zoom = machines.length === 1 ? 17 : machines.length === 0 ? 6 : 12;

  // Evita errori Leaflet (_leaflet_pos) quando non ci sono macchine:
  // in questo caso mostriamo solo una card informativa senza inizializzare la mappa.
  if (machines.length === 0) {
    return (
      <div className="card">
        <div className="card-header fw-semibold d-flex align-items-center justify-content-between">
          <span>
            <i className="bi bi-geo-alt me-2" />
            Mappa macchine {providerLabel}
          </span>
          <span className="badge bg-secondary">0 con coordinate</span>
        </div>
        <div className="card-body">
          <div className="text-muted small">
            Nessuna macchina con coordinate. Aggiungi <code>lat</code> e <code>lng</code> per visualizzarle sulla mappa.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header fw-semibold d-flex align-items-center justify-content-between">
        <span>
          <i className="bi bi-geo-alt me-2" />
          Mappa macchine {providerLabel}
        </span>
        <span className="badge bg-secondary">
          {machines.length} con coordinate
        </span>
      </div>
      <div
        className="card-body p-0 overflow-hidden"
        style={{ borderRadius: "0 0 12px 12px", position: "relative" }}
      >
        <div
          style={{
            width: "100%",
            height: 400,
            borderRadius: 12,
            overflow: "hidden",
            position: "relative",
          }}
        >
          <MapContainer
            center={mapCenter}
            zoom={zoom}
            style={{ height: "100%", width: "100%" }}
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MapBounds machines={machines} />
            {machines.map((m) => (
              <Marker
                key={m.id}
                position={[m.latitude, m.longitude]}
                icon={createIcon()}
              >
                <Popup>
                  <div style={{ padding: 4, minWidth: 180 }}>
                    <div className="fw-bold">{m.name}</div>
                    <div className="small text-muted">{m.provider}</div>
                    {(m.address || m.location) && (
                      <div className="small mt-1 text-muted">
                        {m.address || m.location}
                      </div>
                    )}
                    <div className="mt-2">
                      <span
                        className={`badge ${
                          m.status === "ONLINE"
                            ? "bg-success"
                            : m.status === "ALARM"
                              ? "bg-danger"
                              : "bg-secondary"
                        }`}
                      >
                        {m.status ?? "—"}
                      </span>
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
        {machines.length === 0 && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              background: "white",
              padding: "1rem 1.5rem",
              borderRadius: 8,
              boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
              zIndex: 1000,
              textAlign: "center",
            }}
          >
            <div className="text-muted small">
              Nessuna macchina con coordinate. Aggiungi <code>lat</code> e{" "}
              <code>lng</code> per visualizzarle sulla mappa.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
